// The Vendors page's read, list rules and save: ONE copy, used by the desktop Vendors page
// (pages/VendorManagementPage.jsx) and the phone (mobile/screens/VendorsScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page;
// pages/VendorManagementPage.actions.test.jsx pins the save, components/vendorManagementList.smoke.test.jsx
// the list. Delete stays on the desktop.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

/** GET /vendors (ORDER BY vendor_id; no per-vendor read: a row has every field). Throws the API's sentence. */
export async function loadVendors() {
  const res = await fetch(`${API_BASE}/vendors`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

export const VENDOR_SORTS = Object.freeze([
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'date_newest', label: 'Newest Created' },
  { value: 'date_oldest', label: 'Oldest Created' },
]);

/** Search (name, contact or id; trimmed) and sort. "Created" sorts on vendor_id: GET /vendors returns no
 *  created_at and the id is monotonic (the API's own ORDER BY vendor_id). A new array. */
export function filterAndSortVendors(vendors, { searchTerm = '', sortBy = 'name_asc' } = {}) {
  const term = String(searchTerm || '').trim().toLowerCase();
  const result = (vendors || []).filter((v) => {
    if (!term) return true;
    return (v.name || '').toLowerCase().includes(term) ||
      (v.contact_name || '').toLowerCase().includes(term) ||
      String(v.vendor_id).includes(term);
  });
  result.sort((a, b) => {
    switch (sortBy) {
      case 'name_asc': return (a.name || '').localeCompare(b.name || '');
      case 'name_desc': return (b.name || '').localeCompare(a.name || '');
      case 'date_newest': return (b.vendor_id || 0) - (a.vendor_id || 0);
      case 'date_oldest': return (a.vendor_id || 0) - (b.vendor_id || 0);
      default: return 0;
    }
  });
  return result;
}

/** The form's fields: empty for a new vendor, else the vendor's (null → ""). */
export function vendorForm(v) {
  return {
    name: v?.name || '',
    contact_name: v?.contact_name || '',
    phone: v?.phone || '',
    email: v?.email || '',
    address: v?.address || '',
    city: v?.city || '',
    state: v?.state || '',
    zip_code: v?.zip_code || '',
    notes: v?.notes || '',
    is_service: !!v?.is_service,
    service_types: v?.service_types || '',
  };
}

/** The request body: every text trimmed, blank = null. */
export function vendorPayload(form) {
  const t = (s) => String(s || '').trim() || null;
  return {
    name: String(form.name || '').trim(),
    contact_name: t(form.contact_name),
    phone: t(form.phone),
    email: t(form.email),
    address: t(form.address),
    city: t(form.city),
    state: t(form.state),
    zip_code: t(form.zip_code),
    notes: t(form.notes),
    is_service: !!form.is_service,
    service_types: t(form.service_types),
  };
}

/** Save a vendor: PATCH /vendors/{vendorId} for one picked from the list, else POST /vendors. A missing
 *  name is said through ui.alert and sends nothing; the API's refusal comes back as `error` (the desktop
 *  shows it in its banner). setBusy(true) only once the request goes. Resolves {done, error?}. */
export async function saveVendor({ vendorId = null, form, ui, setBusy = () => {} }) {
  if (!String(form.name || '').trim()) {
    await ui.alert('Vendor name is required.');
    return { done: false };
  }
  const payload = vendorPayload(form);
  setBusy(true);
  try {
    const res = await fetch(vendorId ? `${API_BASE}/vendors/${vendorId}` : `${API_BASE}/vendors`, {
      method: vendorId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    await ui.alert(vendorId ? 'Vendor updated.' : 'Vendor created.');
    return { done: true };
  } catch (err) {
    return { done: false, error: err.message };
  } finally {
    setBusy(false);
  }
}
